import type { LifeEventKind } from '@projections/engine';
import { useFieldArray } from 'react-hook-form';
import { Field, FieldRow } from '../../../forms/fields';
import { TimePointField } from '../fields/TimePointField';
import type { SectionProps } from '../sectionProps';

/**
 * LIFE EVENTS section — a `useFieldArray` over `events`. Each row edits a `LifeEvent`: `id` (the
 * anchor key other sections' TimePoints reference — surfaced and editable, kept stable/unique),
 * `name`, `kind` (retirement | house_purchase | education | custom), the `at` TimePoint (shared
 * field), an optional `oneTimeFlow` (negative = outflow, e.g. a house deposit), and an optional
 * `personId` from the household.
 */

const EVENT_KINDS: ReadonlyArray<{ value: LifeEventKind; label: string }> = [
  { value: 'retirement', label: 'Retirement' },
  { value: 'house_purchase', label: 'House purchase' },
  { value: 'education', label: 'Education' },
  { value: 'custom', label: 'Custom' },
];

export function EventsSection({ form }: SectionProps): React.ReactElement {
  const { register, control, watch, formState } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'events' });
  const errors = formState.errors.events;
  const people = watch('household.people');

  return (
    <section>
      <h2>Life events</h2>
      <p style={{ color: 'var(--ink-muted)', marginBottom: 'var(--sp-6)' }}>
        Anchors like retirement or a house purchase. Income and expense streams can start or end
        relative to these by their <strong>id</strong>.
      </p>

      {typeof errors?.message === 'string' && (
        <p className="field__error" style={{ marginBottom: 'var(--sp-4)' }}>
          {errors.message}
        </p>
      )}
      {typeof errors?.root?.message === 'string' && (
        <p className="field__error" style={{ marginBottom: 'var(--sp-4)' }}>
          {errors.root.message}
        </p>
      )}

      {fields.map((f, i) => {
        const rowErrors = errors?.[i];
        return (
          <div key={f.id} className="card" style={{ marginBottom: 'var(--sp-4)' }}>
            <FieldRow>
              <div style={{ flex: 1, minWidth: 130 }}>
                <Field
                  label="Id (anchor)"
                  error={rowErrors?.id}
                  htmlFor={`events.${i}.id`}
                  hint="Referenced by streams."
                >
                  <input id={`events.${i}.id`} className="input" {...register(`events.${i}.id`)} />
                </Field>
              </div>
              <div style={{ flex: 2, minWidth: 160 }}>
                <Field label="Name" error={rowErrors?.name} htmlFor={`events.${i}.name`}>
                  <input
                    id={`events.${i}.name`}
                    className="input"
                    {...register(`events.${i}.name`)}
                  />
                </Field>
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <Field label="Kind" error={rowErrors?.kind} htmlFor={`events.${i}.kind`}>
                  <select
                    id={`events.${i}.kind`}
                    className="select"
                    {...register(`events.${i}.kind`)}
                  >
                    {EVENT_KINDS.map((k) => (
                      <option key={k.value} value={k.value}>
                        {k.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </FieldRow>

            <FieldRow>
              <div style={{ flex: 1, minWidth: 220 }}>
                <TimePointField
                  form={form}
                  name={`events.${i}.at`}
                  label="When"
                  error={rowErrors?.at}
                />
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <Field
                  label="One-time flow ฿"
                  error={rowErrors?.oneTimeFlow}
                  htmlFor={`events.${i}.oneTimeFlow`}
                  hint="Negative = outflow."
                >
                  <input
                    id={`events.${i}.oneTimeFlow`}
                    className="input input--num"
                    type="number"
                    {...register(`events.${i}.oneTimeFlow`, {
                      setValueAs: emptyToUndefinedNumber,
                    })}
                  />
                </Field>
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <Field label="Person" error={rowErrors?.personId} htmlFor={`events.${i}.personId`}>
                  <select
                    id={`events.${i}.personId`}
                    className="select"
                    {...register(`events.${i}.personId`, {
                      setValueAs: emptyToUndefinedString,
                    })}
                  >
                    <option value="">—</option>
                    {people.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name.length > 0 ? p.name : p.id}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </FieldRow>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--sp-3)' }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => remove(i)}
                aria-label={`Remove event ${i + 1}`}
              >
                Remove event
              </button>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className="btn"
        onClick={() =>
          append({
            id: `event${fields.length + 1}`,
            name: '',
            kind: 'custom',
            at: { kind: 'year', year: form.getValues('startYear') },
          })
        }
      >
        + Add event
      </button>
    </section>
  );
}

/** Empty/blank → undefined; else the parsed number. */
function emptyToUndefinedNumber(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
}

/** Empty → undefined; else the string (keeps optional `personId` truly optional). */
function emptyToUndefinedString(value: unknown): string | undefined {
  return value === '' || value === null || value === undefined ? undefined : String(value);
}
